'use client';

import { useEffect, useRef, useState } from 'react';
import { IconX } from '@tabler/icons-react';
import classNames from 'classnames';
import maplibregl, { Marker } from 'maplibre-gl';
import { createRoot } from 'react-dom/client';
import { MantineProvider, Popover, UnstyledButton } from '@mantine/core';
import { BaseMarkerConfig } from './markerconfig';
import styles from './marker.module.css';

interface BaseMarkerProps {
  map: maplibregl.Map;
  geoJsonData: {
    features: {
      geometry: { coordinates: any; type: string };
      properties?: Record<string, any>;
    }[];
  };
  config: BaseMarkerConfig;
  zIndex: string;
}

interface MarkerInfo {
  marker: Marker;
  element: HTMLDivElement;
}

export function MarkerAndPopover({ map, geoJsonData, config, zIndex }: BaseMarkerProps) {
  const markersRef = useRef<MarkerInfo[]>([]);

  useEffect(() => {
    markersRef.current.forEach(({ marker }) => marker.remove());
    markersRef.current = [];

    geoJsonData.features.forEach((feature, index) => {
      const parsedCoordinates = feature.geometry.coordinates.map((coord: string | number) =>
        typeof coord === 'string' ? parseFloat(coord) : coord
      );
      const coordinates: [number, number] = config
        .getCoordinates(parsedCoordinates)
        .slice(0, 2) as [number, number];

      const icon = config.getIcon(feature);

      const el = document.createElement('div');
      el.style.zIndex = zIndex;
      el.style.cursor = 'pointer';
      el.id = `marker-${index}`;

      const root = createRoot(el);

      const MarkerContent = () => {
        const [opened, setOpened] = useState(false);

        const { title, details } = config.getPopoverContent(feature);

        return (
          <MantineProvider>
            <Popover
              width={400}
              position="top"
              clickOutsideEvents={['mouseup', 'touchend']}
              offset={3}
              opened={opened}
              onChange={setOpened}
              zIndex={15}
            >
              <Popover.Target>
                <UnstyledButton
                  className={classNames(
                    styles.icon,
                    styles.iconButton,
                    styles[config.iconClass],
                    styles[config.buttonClass],
                    {
                      [styles['iconButton-open']]: opened,
                    }
                  )}
                  onClick={() => setOpened((o: any) => !o)}
                >
                  {icon}
                </UnstyledButton>
              </Popover.Target>
              <Popover.Dropdown className={styles.popover}>
                <div className={styles.popoverTitle}>
                  <div className={styles.iconAndText}>
                    <div className={`${styles.icon} ${styles[config.iconClass]}`}>{icon}</div>
                    {title}
                  </div>
                  <UnstyledButton className={styles.closeButton} onClick={() => setOpened(false)}>
                    <IconX aria-label="Close" />
                  </UnstyledButton>
                </div>
                <hr className={styles.divider} />
                {details}
              </Popover.Dropdown>
            </Popover>
          </MantineProvider>
        );
      };

      root.render(<MarkerContent />);

      const marker = new maplibregl.Marker({ element: el }).setLngLat(coordinates).addTo(map);

      markersRef.current.push({ marker, element: el });
    });

    return () => {
      markersRef.current.forEach(({ marker }) => marker.remove());
    };
  }, [map, geoJsonData, config]);

  return <></>;
}
